/* In-memory mocked client for Automated Workflows — no real backend exists for this domain yet. */

import { IWorkflowsClient } from './IWorkflowsClient';
import {
  WorkflowTriggerDto,
  WorkflowConditionFieldDto,
  WorkflowActionDto,
  AutomatedWorkflowDto,
  CreateUpdateWorkflowDto,
  WorkflowActionConfigDto,
  WorkflowConditionPairDto,
  WorkflowTriggerType,
  WorkflowConditionField,
  WorkflowConditionOperator,
  WorkflowActionType,
  WorkflowUserDto,
  WorkflowRoleDto,
  WorkflowOrderTypeDto,
  WorkflowSupplierDto,
  WorkflowStockListDto,
  WorkflowZoneDto,
  WorkflowReportTypeDto,
  WorkflowTaskTemplateDto,
} from './workflowsTypes';

const defaultConditionPair = (
  id: number,
  field: WorkflowConditionField,
  operator: WorkflowConditionOperator,
  value: string,
): WorkflowConditionPairDto => ({
  id,
  criteria1: {
    id: id * 10 + 1,
    field,
    operator,
    value,
  },
  criteria2: null,
  criteriaLogic: 'AND',
});

const createActionConfig = (type: WorkflowActionType, params: Record<string, string>): WorkflowActionConfigDto => ({
  type,
  params,
});

const mockTriggers: WorkflowTriggerDto[] = [
  { id: 'trigger-stock-low', name: 'Stock Level Low', description: 'Triggers when stock drops below threshold', type: 'StockLevelLow', category: 'Inventory' },
  { id: 'trigger-order-placed', name: 'Order Placed', description: 'Triggers when an order is placed', type: 'OrderPlaced', category: 'Orders' },
  { id: 'trigger-delivery-received', name: 'Delivery Received', description: 'Triggers when a delivery is confirmed', type: 'OrderReceived', category: 'Orders' },
  { id: 'trigger-audit-failed', name: 'Audit Failed', description: 'Triggers when audit score falls below threshold', type: 'TaskOverdue', category: 'Compliance' },
];

const mockConditionFields: WorkflowConditionFieldDto[] = [
  { id: 'field-product-category', name: 'Product Category', description: 'Filter workflows by product category', field: 'ProductCategory', dataType: 'enum', enumValues: ['Food', 'Beverage', 'Supplies'], category: 'Inventory', applicableTriggers: ['StockLevelLow', 'StockLevelCritical'] },
  { id: 'field-store-number', name: 'Store Number', description: 'Filter workflows by store number', field: 'StoreNumber', dataType: 'string', category: 'General' },
  { id: 'field-stock-level', name: 'Stock Level', description: 'Filter workflows by stock level threshold', field: 'StockLevel', dataType: 'number', category: 'Inventory', applicableTriggers: ['StockLevelLow', 'StockLevelCritical'] },
  { id: 'field-order-type', name: 'Order Type', description: 'Filter workflows by order type', field: 'OrderType', dataType: 'enum', enumValues: ['Fresh & Daily', 'Ambient', 'Frozen'], category: 'Orders', applicableTriggers: ['OrderPlaced', 'OrderReceived', 'OrderCancelled'] },
];

const mockActions: WorkflowActionDto[] = [
  { id: 'action-send-notification', name: 'Send Notification', description: 'Send in-app notification to users', type: 'SendNotification', category: 'Communication', requiredParams: ['userId', 'message'], optionalParams: ['title'] },
  { id: 'action-create-task', name: 'Create Task', description: 'Create a follow-up task automatically', type: 'CreateTask', category: 'Tasks', requiredParams: ['taskTemplateId'], optionalParams: ['assignedUserId', 'roleId'] },
  { id: 'action-create-order', name: 'Create Auto Order', description: 'Create a replenishment order automatically', type: 'CreateOrder', category: 'Orders', requiredParams: ['supplierId'], optionalParams: ['orderTypeId'] },
  { id: 'action-escalate-task', name: 'Escalate Task', description: 'Escalate to manager when condition is met', type: 'EscalateTask', category: 'Tasks', requiredParams: ['roleId'], optionalParams: ['notes'] },
];

const mockWorkflows: AutomatedWorkflowDto[] = [
  {
    id: 1, name: 'Low Stock Alert', description: 'Notify managers when stock is low', franchiseId: 1, storeNumbers: ['001'], isActive: true, trigger: 'StockLevelLow',
    conditionPair1: defaultConditionPair(1, 'StockLevel', 'LessThan', '5'), conditionPair2: null, conditionPairLogic: 'AND',
    action: createActionConfig('SendNotification', { userId: '1', message: 'Stock is low' }),
    createdAtUtc: '2024-01-15T10:00:00Z', createdByUserId: 1, updatedAtUtc: '2024-01-15T10:00:00Z', updatedByUserId: 1,
    lastTriggeredAtUtc: '2024-01-18T09:30:00Z', triggerCount: 7,
  },
  {
    id: 2, name: 'Auto Reorder', description: 'Auto-create order for beverage category', franchiseId: 1, storeNumbers: ['001', '004'], isActive: true, trigger: 'StockLevelCritical',
    conditionPair1: defaultConditionPair(2, 'ProductCategory', 'Equals', 'Beverage'), conditionPair2: null, conditionPairLogic: 'AND',
    action: createActionConfig('CreateOrder', { supplierId: '1', orderTypeId: '2' }),
    createdAtUtc: '2024-02-20T14:30:00Z', createdByUserId: 1, updatedAtUtc: '2024-02-22T09:10:00Z', updatedByUserId: 2,
    lastTriggeredAtUtc: null, triggerCount: 0,
  },
  {
    id: 3, name: 'Audit Failure Escalation', description: 'Escalate overdue compliance tasks', franchiseId: 1, storeNumbers: null, isActive: false, trigger: 'TaskOverdue',
    conditionPair1: defaultConditionPair(3, 'TaskStatus', 'Equals', 'Overdue'), conditionPair2: null, conditionPairLogic: 'AND',
    action: createActionConfig('EscalateTask', { roleId: '2', notes: 'Auto-escalated' }),
    createdAtUtc: '2024-03-10T09:00:00Z', createdByUserId: 2, updatedAtUtc: null, updatedByUserId: null,
    lastTriggeredAtUtc: null, triggerCount: 0,
  },
];

let nextId = 100;

export class WorkflowsClientMocked implements IWorkflowsClient {
  async getTriggers(): Promise<WorkflowTriggerDto[]> {
    return mockTriggers;
  }

  async getConditionFields(): Promise<WorkflowConditionFieldDto[]> {
    return mockConditionFields;
  }

  async getConditionFieldsForTrigger(triggerType: WorkflowTriggerType): Promise<WorkflowConditionFieldDto[]> {
    return mockConditionFields.filter((f) => !f.applicableTriggers || f.applicableTriggers.includes(triggerType));
  }

  async getActions(): Promise<WorkflowActionDto[]> {
    return mockActions;
  }

  async getWorkflows(): Promise<AutomatedWorkflowDto[]> {
    return mockWorkflows;
  }

  async getWorkflow(id: number): Promise<AutomatedWorkflowDto | null> {
    return mockWorkflows.find((w) => w.id === id) ?? null;
  }

  async createWorkflow(dto: CreateUpdateWorkflowDto): Promise<AutomatedWorkflowDto> {
    const newWf: AutomatedWorkflowDto = {
      id: ++nextId,
      name: dto.name,
      description: dto.description,
      franchiseId: dto.franchiseId,
      storeNumbers: dto.storeNumbers ?? null,
      isActive: dto.isActive,
      trigger: dto.trigger,
      conditionPair1: dto.conditionPair1,
      conditionPair2: dto.conditionPair2 ?? null,
      conditionPairLogic: dto.conditionPairLogic,
      action: dto.action,
      createdAtUtc: new Date().toISOString(),
      createdByUserId: 1,
      updatedAtUtc: null,
      updatedByUserId: null,
      lastTriggeredAtUtc: null,
      triggerCount: 0,
    };
    mockWorkflows.push(newWf);
    return newWf;
  }

  async updateWorkflow(id: number, dto: CreateUpdateWorkflowDto): Promise<AutomatedWorkflowDto> {
    const idx = mockWorkflows.findIndex((w) => w.id === id);
    if (idx === -1) throw new Error(`Workflow ${id} not found`);
    mockWorkflows[idx] = {
      ...mockWorkflows[idx],
      name: dto.name,
      description: dto.description,
      franchiseId: dto.franchiseId,
      storeNumbers: dto.storeNumbers ?? null,
      isActive: dto.isActive,
      trigger: dto.trigger,
      conditionPair1: dto.conditionPair1,
      conditionPair2: dto.conditionPair2 ?? null,
      conditionPairLogic: dto.conditionPairLogic,
      action: dto.action,
      updatedAtUtc: new Date().toISOString(),
      updatedByUserId: 1,
    };
    return mockWorkflows[idx];
  }

  async deleteWorkflow(id: number): Promise<void> {
    const idx = mockWorkflows.findIndex((w) => w.id === id);
    if (idx === -1) throw new Error(`Workflow ${id} not found`);
    mockWorkflows.splice(idx, 1);
  }

  async toggleWorkflowActive(id: number, isActive: boolean): Promise<AutomatedWorkflowDto> {
    const wf = mockWorkflows.find((w) => w.id === id);
    if (!wf) throw new Error(`Workflow ${id} not found`);
    wf.isActive = isActive;
    wf.updatedAtUtc = new Date().toISOString();
    wf.updatedByUserId = 1;
    return wf;
  }

  /* ---- Reference Data ---- */

  async getUsers(): Promise<WorkflowUserDto[]> {
    return [
      { id: 1, name: 'John Smith', email: 'john@store.com' },
      { id: 2, name: 'Jane Doe', email: 'jane@store.com' },
    ];
  }

  async getRoles(): Promise<WorkflowRoleDto[]> {
    return [
      { id: 1, name: 'Store Manager', description: 'Store-level manager role' },
      { id: 2, name: 'Area Manager', description: 'Area-level manager role' },
    ];
  }

  async getOrderTypes(): Promise<WorkflowOrderTypeDto[]> {
    return [
      { id: 1, name: 'Fresh & Daily' },
      { id: 2, name: 'Ambient' },
      { id: 3, name: 'Frozen' },
    ];
  }

  async getSuppliers(): Promise<WorkflowSupplierDto[]> {
    return [
      { id: 1, name: 'Dairy Direct', code: 'SUP001' },
      { id: 2, name: 'Coffee Wholesale Ltd', code: 'SUP002' },
    ];
  }

  async getStockLists(): Promise<WorkflowStockListDto[]> {
    return [
      { id: 1, name: 'Default Stock List' },
      { id: 2, name: 'Beverage Stock List' },
    ];
  }

  async getZones(): Promise<WorkflowZoneDto[]> {
    return [
      { id: 1, name: 'Front of House' },
      { id: 2, name: 'Kitchen' },
    ];
  }

  async getReportTypes(): Promise<WorkflowReportTypeDto[]> {
    return [
      { id: 'waste-summary', name: 'Waste Summary' },
      { id: 'audit-compliance', name: 'Audit Compliance' },
    ];
  }

  async getTaskTemplates(): Promise<WorkflowTaskTemplateDto[]> {
    return [
      { id: 1, name: 'Stock Investigation', category: 'Inventory' },
      { id: 2, name: 'Supplier Follow-up', category: 'Operations' },
    ];
  }
}
